// Haushaltstechnik & Sonderposten can flip sign year-to-year. To prevent the
// stacked area polygon from slicing diagonally across the rest of the stack on
// sign flips, we render it as two adjacent stacked Areas (a positive half and
// a negative half) and merge them back into a single tooltip row at read time.
//
// DO NOT TOUCH the dataKey naming pattern (`${TYPE}__pos` / `${TYPE}__neg`)
// without verifying:
//   1. The chart's data construction still emits both halves per year.
//   2. The legend still shows one entry per type, not two.
//   3. The tooltip merger still finds and combines them.
//   4. After a recharts upgrade, snapshot a year with HHT sign flips
//      (e.g. 2020→2021 in the Bundeshaushalt) and confirm the polygon meets
//      the x-axis at the flip year instead of crossing diagonally.

export const HAUSHALTSTECHNIK_TYPE = 'Haushaltstechnik & Sonderposten';
export const HAUSHALTSTECHNIK_POS = `${HAUSHALTSTECHNIK_TYPE}__pos`;
export const HAUSHALTSTECHNIK_NEG = `${HAUSHALTSTECHNIK_TYPE}__neg`;

interface SplitEntry {
  name: string;
  value: number;
  color: string;
  dataKey: string;
}

/**
 * Merge the positive and negative halves of a sign-split tooltip entry back
 * into a single signed entry. Insert position is the leftmost half's index so
 * the row keeps its place in the tooltip ordering.
 *
 * Idempotent on entries that don't contain either half.
 *
 * Generic over T so callers can carry extra fields (e.g. per-entry tooltip
 * metadata) without losing them through this merger.
 */
export function mergeHaushaltstechnikEntries<T extends SplitEntry>(entries: T[]): T[] {
  const posIdx = entries.findIndex(e => e.dataKey === HAUSHALTSTECHNIK_POS);
  const negIdx = entries.findIndex(e => e.dataKey === HAUSHALTSTECHNIK_NEG);

  if (posIdx === -1 && negIdx === -1) return entries;

  const pos = posIdx !== -1 ? entries[posIdx] : null;
  const neg = negIdx !== -1 ? entries[negIdx] : null;
  const insertAt = pos && neg ? Math.min(posIdx, negIdx) : (pos ? posIdx : negIdx);

  const merged = {
    ...(pos ?? neg)!,
    name: HAUSHALTSTECHNIK_TYPE,
    value: (pos?.value ?? 0) + (neg?.value ?? 0),
    color: (pos?.color ?? neg?.color) as string,
    dataKey: HAUSHALTSTECHNIK_TYPE,
  } as T;

  const filtered = entries.filter((_, i) => i !== posIdx && i !== negIdx);
  filtered.splice(insertAt, 0, merged);
  return filtered;
}
